import { ActionIcon, createStyles, Tabs } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { RpiCard } from "../../rpi-core/card/RpiCard";
import { Button, tokens } from "@blurb/codex-react";
import { GraphQL } from "@rpi/openapi-api";
import { IconV3 } from "@rpi/openapi-core";
import { Body, Heading } from "../../components/Typography";
import { IdLine } from "../../components/IdLine";
import React from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { formatErrorDetail } from "../../utils/query.util";
import { OrderStatusCell } from "../../components/cells/OrderStatusCellV2";
import { BackLink } from "../../components/BackLink";
import { CancelOrderDrawer } from "./CancelOrderDrawer";
import { OrderActionsMenu, useOverridePaymentStatusFlow, useRedriveOrderFlow } from "./OrderActionsMenu";
import { ReprintDrawer } from "./ReprintDrawer";
import { RefundDrawer } from "./components/refund/RefundDrawer";
import { OrderViewOutletContext } from "./tabs/order-details/shared";
import { SkeletonField } from "../../components/skeleton/SkeletonField";

const useStyles = createStyles(() => ({
  container: {
    height: "100%",
    overflow: "auto" as const,
    padding: tokens.spacing[20],
    display: "flex",
    flexDirection: "column" as const,
    gap: tokens.spacing[6],
  },
  card: {
    display: "flex",
    flexDirection: "column" as const,
    padding: tokens.spacing[6],
    gap: tokens.spacing[6],
    // 420px floor keeps the Tabs.List on one row; the page scrolls horizontally below this.
    minWidth: 420,
  },
  errorCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    overflow: "hidden",
    padding: tokens.spacing[6],
    gap: tokens.spacing[4],
    alignItems: "center",
    justifyContent: "center",
  },
  loadingContainer: {
    flex: 1,
    position: "relative" as const,
  },
  tabsContainer: {
    display: "flex",
    flexDirection: "column" as const,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: tokens.spacing[4],
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-start",
    gap: tokens.spacing[1],
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[2],
    minWidth: 0,
  },
  idGroup: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacing[1],
    minWidth: 0,
  },
  subtleText: {
    color: tokens.color.semantic.text.subtle,
  },
  monoId: {
    fontFamily: tokens.font.family.mono,
    // Long ids wrap across lines instead of forcing horizontal scroll.
    wordBreak: "break-all" as const,
  },
}));

const ORDER_TABS = [
  { label: "Details", path: "details" },
  { label: "Items", path: "items" },
  { label: "Event Logs", path: "event-logs" },
  { label: "Shipments", path: "shipments" },
] as const;

function findActiveTab(pathname: string, basePath: string): string {
  return ORDER_TABS.find((tab) => pathname === `${basePath}/${tab.path}`)?.path ?? "details";
}

export const OrderView: React.FC = () => {
  const { customerPublicId, customerOrderId } = useParams();
  if (!customerPublicId || !customerOrderId) {
    throw new Error("Route error, order details route requires the order identification params");
  }
  const user = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [reprintOpened, setReprintOpened] = React.useState(false);
  const [refundOpened, setRefundOpened] = React.useState(false);
  const [cancelOpened, setCancelOpened] = React.useState(false);

  const { data, isLoading, error, refetch } = GraphQL.useOrderMinimalQuery({
    orderIdentifier: {
      customerPublicId,
      customerOrderId,
    },
    isSandbox: user.isSandbox,
  });
  const order = data?.orderByIdentifier;
  // Prefer the loaded order's own id over the route param, which may differ from order.customerOrderId.
  const displayCustomerOrderId = order?.customerOrderId ?? customerOrderId;
  const primaryId = order?.orderPublicId ?? displayCustomerOrderId;
  // Backend-filled orders carry the public id in both fields; omit the duplicate line instead of showing it twice
  const isCustomerOrderIdRedundant = order?.orderPublicId === displayCustomerOrderId;
  const clipboard = useClipboard({ timeout: 1500 });

  const outletContext: OrderViewOutletContext = {
    customerPublicId: customerPublicId,
    customerOrderId: customerOrderId,
  };

  // Hoist the override-payment-status modal above the early returns and
  // outside the actions Menu.Dropdown so it survives the menu's open/close
  // cycle. Picking a status closes the menu; if the modal lived inside the
  // dropdown subtree it would unmount before the user could confirm.
  const { openConfirm: openOverridePaymentStatus, modal: overridePaymentStatusModal } = useOverridePaymentStatusFlow(
    order ?? { customerOrderId: "", paymentStatus: null }
  );
  const { openConfirm: openRedrive, modal: redriveModal } = useRedriveOrderFlow(order ?? { customerOrderId: "" });

  const { classes } = useStyles();

  const orderBasePath = getRoutePath("order", {
    customerPublicId: customerPublicId ?? "",
    customerOrderId: customerOrderId ?? "",
  });
  const activeTab = findActiveTab(location.pathname, orderBasePath);

  if (error || (!isLoading && !order)) {
    const errorDetail = formatErrorDetail(error);
    return (
      <div className={classes.container}>
        <BackLink to={getRoutePath("orders")}>Back to All Orders</BackLink>
        <RpiCard className={classes.errorCard}>
          <Body>Failed to load order.</Body>
          {errorDetail && (
            <Body size="sm" className={classes.subtleText}>
              {errorDetail}
            </Body>
          )}
          <Button variant="outlined" intent="neutral" onClick={() => refetch()}>
            Retry
          </Button>
        </RpiCard>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <BackLink to={getRoutePath("orders")}>Back to All Orders</BackLink>

      <RpiCard className={classes.card}>
        <div className={classes.header}>
          <div className={classes.headerLeft}>
            <div className={classes.titleRow}>
              {order ? (
                <div className={classes.idGroup}>
                  <Heading size="xl" weight="bold" className={classes.monoId} data-testid="order-primary-id">
                    {primaryId}
                  </Heading>
                  <ActionIcon
                    size="sm"
                    variant="transparent"
                    data-testid="order-primary-id-copy"
                    onClick={() => clipboard.copy(primaryId)}
                  >
                    <IconV3 icon="content_copy" size={18} />
                  </ActionIcon>
                </div>
              ) : (
                <SkeletonField />
              )}
              {order ? <OrderStatusCell status={order.publicOrderStatus} /> : <SkeletonField />}
            </div>
            {order && !isCustomerOrderIdRedundant && (
              <IdLine
                label="Customer Order ID"
                value={displayCustomerOrderId}
                testId="order-customer-order-id"
                copyTestId="order-customer-order-id-copy"
              />
            )}
            {user.is.SUPER_ADMIN && order?.uosOrderId && order.uosOrderId !== order.orderPublicId && (
              <IdLine
                label="UOS Order ID"
                value={order.uosOrderId}
                testId="order-uos-order-id"
                copyTestId="order-uos-order-id-copy"
              />
            )}
          </div>

          {order ? (
            <OrderActionsMenu
              order={order}
              onReprintClick={() => setReprintOpened(true)}
              onRefundClick={() => setRefundOpened(true)}
              onCancelClick={() => setCancelOpened(true)}
              onRedriveClick={openRedrive}
              onOverridePaymentStatusPick={openOverridePaymentStatus}
            />
          ) : (
            <SkeletonField />
          )}
        </div>

        <Tabs
          value={activeTab}
          onTabChange={(value) => {
            if (value) navigate(`${orderBasePath}/${value}`);
          }}
          className={classes.tabsContainer}
        >
          <Tabs.List>
            {ORDER_TABS.map((tab) => (
              <Tabs.Tab key={tab.path} value={tab.path} disabled={isLoading}>
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Outlet context={outletContext} />
        </Tabs>
      </RpiCard>

      {user.is.SUPER_ADMIN && customerPublicId && customerOrderId && order?.canReprint && (
        <ReprintDrawer
          opened={reprintOpened}
          onClose={() => setReprintOpened(false)}
          customerPublicId={customerPublicId}
          customerOrderId={customerOrderId}
          isSandbox={user.isSandbox}
        />
      )}
      {user.is.SUPER_ADMIN && customerPublicId && customerOrderId && order?.canRefund && (
        <RefundDrawer
          opened={refundOpened}
          onClose={() => setRefundOpened(false)}
          customerPublicId={customerPublicId}
          customerOrderId={customerOrderId}
          isSandbox={user.isSandbox}
        />
      )}
      {order && <CancelOrderDrawer opened={cancelOpened} onClose={() => setCancelOpened(false)} order={order} />}
      {overridePaymentStatusModal}
      {redriveModal}
    </div>
  );
};
